import { Chess, type Square } from 'chess.js';
import type { Color, PromotionPiece } from '../../shared/protocol.ts';

export const START_FEN = new Chess().fen();

export interface MoveLike {
  from: string;
  to: string;
  promotion?: PromotionPiece;
}

/** FEN after each ply: fens[0] is the start position, fens[i] is after move i. */
export function positionsAfter(moves: readonly MoveLike[]): string[] {
  const chess = new Chess();
  const fens = [chess.fen()];
  for (const m of moves) {
    chess.move({ from: m.from, to: m.to, promotion: m.promotion });
    fens.push(chess.fen());
  }
  return fens;
}

export type PieceCounts = Record<'p' | 'n' | 'b' | 'r' | 'q', number>;

const START_COUNTS: PieceCounts = { p: 8, n: 2, b: 2, r: 2, q: 1 };
const VALUES: PieceCounts = { p: 1, n: 3, b: 3, r: 5, q: 9 };
const PIECE_ORDER = ['p', 'n', 'b', 'r', 'q'] as const;

export interface MaterialSummary {
  /** Opponent pieces each side has captured. */
  captured: Record<Color, PieceCounts>;
  /** Material lead per side (0 for the side that is behind). */
  lead: Record<Color, number>;
}

/** Captured pieces derived from the position (promotions handled naturally). */
export function materialSummary(fen: string): MaterialSummary {
  const onBoard: Record<Color, PieceCounts> = {
    w: { p: 0, n: 0, b: 0, r: 0, q: 0 },
    b: { p: 0, n: 0, b: 0, r: 0, q: 0 },
  };
  const placement = fen.split(' ')[0];
  for (const ch of placement) {
    const lower = ch.toLowerCase() as keyof PieceCounts;
    if (!(lower in START_COUNTS)) continue;
    onBoard[ch === lower ? 'b' : 'w'][lower] += 1;
  }

  const capturedBy = (color: Color): PieceCounts => {
    const victim = onBoard[color === 'w' ? 'b' : 'w'];
    const out = { p: 0, n: 0, b: 0, r: 0, q: 0 };
    for (const p of PIECE_ORDER) out[p] = Math.max(0, START_COUNTS[p] - victim[p]);
    return out;
  };

  const score = (c: Color) => PIECE_ORDER.reduce((sum, p) => sum + onBoard[c][p] * VALUES[p], 0);
  const diff = score('w') - score('b');

  return {
    captured: { w: capturedBy('w'), b: capturedBy('b') },
    lead: { w: Math.max(0, diff), b: Math.max(0, -diff) },
  };
}

export function checkedKingSquare(chess: Chess): Square | null {
  if (!chess.inCheck()) return null;
  const turn = chess.turn();
  for (const row of chess.board()) {
    for (const sq of row) {
      if (sq && sq.type === 'k' && sq.color === turn) return sq.square;
    }
  }
  return null;
}

export function isPromotionMove(chess: Chess, from: string, to: string): boolean {
  const piece = chess.get(from as Square);
  if (!piece || piece.type !== 'p') return false;
  return (piece.color === 'w' && to[1] === '8') || (piece.color === 'b' && to[1] === '1');
}

export function legalTargets(chess: Chess, from: string): string[] {
  try {
    return chess.moves({ square: from as Square, verbose: true }).map((m) => m.to);
  } catch {
    return [];
  }
}

/** Clock text: h:mm:ss over an hour, m:ss normally, tenths under 10 seconds. */
export function formatClock(ms: number): string {
  const clamped = Math.max(0, ms);
  if (clamped < 10_000) {
    const tenths = Math.floor(clamped / 100);
    return `0:0${Math.floor(tenths / 10)}.${tenths % 10}`;
  }
  const total = Math.ceil(clamped / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}
